import type { AnalyticsEventPayload, UIAnalyticsEvent } from '@atlaskit/analytics-next';
import type { ProjectType } from '@atlassian/jira-common-constants';
import {
	getUserProperty,
	setUserProperties,
} from '@atlassian/jira-common-rest/src/api/latest/user-properties';
import type { FetchError } from '@atlassian/jira-fetch';
import {
	fireGetUserPropertyFailEvent,
	fireGetUserPropertySuccessEvent,
	fireSetUserPropertyFailEvent,
	fireSetUserPropertySuccessEvent,
} from '../../common/utils';
import { KEY_USER_PROPERTIES_DISMISS, KEY_USER_PROPERTIES_MODAL_SHOWN } from '../../constants';

export type CreateAnalyticsEvent = (payload: AnalyticsEventPayload) => UIAnalyticsEvent;
type Payload = Partial<Record<string, unknown>>;

type DismissedUserPropertyShape = {
	dismissedOn?: string;
};

type ViewedUserPropertyShape = {
	viewedOn?: string;
};

const getViewedPropertyKey = (projectType: ProjectType) =>
	`${KEY_USER_PROPERTIES_MODAL_SHOWN}-${projectType}`;
const getDismissedPropertyKey = (projectType: ProjectType) =>
	`${KEY_USER_PROPERTIES_DISMISS}-${projectType}`;

export const getModalViewedProperty = async (
	createAnalyticsEvent: CreateAnalyticsEvent,
	projectType: ProjectType,
	atlassianAccountId: string,
): Promise<boolean> => {
	const propertyKey = getViewedPropertyKey(projectType);
	const property = await getProperty<ViewedUserPropertyShape>(
		createAnalyticsEvent,
		atlassianAccountId,
		propertyKey,
	);

	return !!property?.viewedOn;
};

export const getModalDismissedProperty = async (
	createAnalyticsEvent: CreateAnalyticsEvent,
	projectType: ProjectType,
	atlassianAccountId: string,
): Promise<boolean> => {
	const propertyKey = getDismissedPropertyKey(projectType);
	const property = await getProperty<DismissedUserPropertyShape>(
		createAnalyticsEvent,
		atlassianAccountId,
		propertyKey,
	);

	return !!property?.dismissedOn;
};

export const getProperty = <T extends DismissedUserPropertyShape | ViewedUserPropertyShape>(
	createAnalyticsEvent: CreateAnalyticsEvent,
	atlassianAccountId: string,
	propertyKey: string,
): Promise<T> =>
	getUserProperty('', atlassianAccountId, propertyKey)
		.then((res) => {
			fireGetUserPropertySuccessEvent(createAnalyticsEvent, res ?? {});
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			return (res ?? {}) as T;
		})
		.catch((e: FetchError) => {
			// 404 is returned if there is not user data set
			if (e.statusCode !== 404) {
				fireGetUserPropertyFailEvent(e, createAnalyticsEvent);
			}
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			return {} as T;
		});

export const setModalViewedProperty = (
	createAnalyticsEvent: CreateAnalyticsEvent,
	projectType: ProjectType,
	atlassianAccountId: string,
) => {
	const propertyKey = getViewedPropertyKey(projectType);
	const payload: ViewedUserPropertyShape = {
		viewedOn: new Date().toJSON(),
	};
	return setUserProperty(createAnalyticsEvent, atlassianAccountId, propertyKey, payload);
};

export const setModalDismissedProperty = (
	createAnalyticsEvent: CreateAnalyticsEvent,
	projectType: ProjectType,
	atlassianAccountId: string,
) => {
	const propertyKey = getDismissedPropertyKey(projectType);
	const payload: DismissedUserPropertyShape = {
		dismissedOn: new Date().toJSON(),
	};
	return setUserProperty(createAnalyticsEvent, atlassianAccountId, propertyKey, payload);
};

const setUserProperty = (
	createAnalyticsEvent: CreateAnalyticsEvent,
	atlassianAccountId: string,
	propertyKey: string,
	payload: Payload,
) =>
	setUserProperties(
		'',
		atlassianAccountId,
		propertyKey,
		JSON.stringify({
			date: new Date().toJSON(),
			...payload,
		}),
	)
		.then(() => {
			fireSetUserPropertySuccessEvent(createAnalyticsEvent, payload);
		})
		.catch((e: FetchError) => {
			fireSetUserPropertyFailEvent(e, createAnalyticsEvent, payload);
		});
